import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Search, SlidersHorizontal, Sparkles, X } from "lucide-react";
import { API } from "@/config/site";
import { useStore } from "@/context/StoreContext";
import useSeo from "@/hooks/useSeo";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";

const CAT_OG = ["gaming", "entertainment", "food-dining", "shopping", "digital-services", "other"];

export default function Market() {
  const store = useStore();
  const { marketSlug, categorySlug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const queryCategory = params.get("category") || "";
  const queryMarket = params.get("market") || "";
  const effectiveCategory = categorySlug || queryCategory || null;

  const [q, setQ] = useState(params.get("q") || "");
  const [country, setCountry] = useState("");
  const [brand, setBrand] = useState("");
  const [currencyF, setCurrencyF] = useState("");
  const [platform, setPlatform] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [avail, setAvail] = useState("");
  const [sort, setSort] = useState("popular");
  const [showFilters, setShowFilters] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const routeMarket = marketSlug
    ? store.markets?.find((m) => m.slug === marketSlug)
    : null;
  const selectedMarket = routeMarket?.slug || store.market || queryMarket || null;
  const selectedMarketObj = selectedMarket
    ? store.markets?.find((m) => m.slug === selectedMarket)
    : null;
  const invalidMarket =
    (marketSlug || queryMarket) && store.ready && !selectedMarketObj;

  // A market URL is authoritative. Otherwise use the user's persisted header selection.
  useEffect(() => {
    if (routeMarket && store.market !== routeMarket.slug) {
      store.setMarket(routeMarket.slug);
    }
  }, [routeMarket?.slug, store.ready]);

  // If a category is supplied as a query parameter, keep the UI URL/state working.
  useEffect(() => {
    if (categorySlug || !queryCategory) return;
    const validCategory = store.categories?.some((c) => c.slug === queryCategory);
    if (!validCategory) {
      const next = new URLSearchParams(params);
      next.delete("category");
      navigate(next.toString() ? `/cards?${next}` : "/cards", { replace: true });
    }
  }, [categorySlug, queryCategory, store.categories, params, navigate]);

  useSeo({
    title:
      store.ready && effectiveCategory && store.categoryName
        ? `${store.categoryName(effectiveCategory)} Gift Cards ${selectedMarketObj ? `in ${selectedMarketObj.name}` : ""} | ZEYINX`
        : selectedMarketObj?.seo?.title || "All Gift Cards & Digital Products | ZEYINX",
    description:
      store.ready && effectiveCategory && store.categoryName
        ? `Browse ${store.categoryName(effectiveCategory).toLowerCase()} gift cards ${selectedMarketObj ? `available for redemption in ${selectedMarketObj.name}` : "on Zeyinx"}.`
        : selectedMarketObj?.seo?.description || "Browse the full Zeyinx catalog of digital gift cards and vouchers.",
    image:
      effectiveCategory && CAT_OG.includes(effectiveCategory)
        ? `/og/cat-${effectiveCategory}.png`
        : `/og/${selectedMarketObj?.slug || "default"}.png`,
  });

  const now = Date.now();
  const activeBanners = (store.banners || [])
    .filter((b) => b.placement === "market" || b.placement === "both")
    .filter((b) => !b.start_at || Number.isNaN(Date.parse(b.start_at)) || Date.parse(b.start_at) <= now)
    .filter((b) => !b.end_at || Number.isNaN(Date.parse(b.end_at)) || Date.parse(b.end_at) >= now)
    .sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0));
  const banner =
    activeBanners.find((b) => b.market === selectedMarket) ||
    activeBanners.find((b) => b.market === "all");
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const bannerHidden =
    !banner ||
    bannerDismissed ||
    sessionStorage.getItem(`zey_banner_${banner.id}`) === "1";

  const queryParams = useMemo(() => {
    const p = { page, limit: 24 };
    if (selectedMarket) p.market = selectedMarket;
    if (effectiveCategory) p.category = effectiveCategory;
    if (q.trim()) p.q = q.trim();
    if (country) p.country = country;
    if (brand) p.brand = brand;
    if (currencyF) p.currency = currencyF;
    if (platform) p.platform = platform;
    if (minPrice) p.min_price = Number(minPrice);
    if (maxPrice) p.max_price = Number(maxPrice);
    if (avail) p.availability = avail === "in";
    if (sort) p.sort = sort;
    return p;
  }, [selectedMarket, effectiveCategory, q, country, brand, currencyF, platform, minPrice, maxPrice, avail, sort, page]);

  useEffect(() => {
    if (!store.ready) return;
    setLoading(true);
    const t = setTimeout(() => {
      axios
        .get(`${API}/products`, { params: queryParams })
        .then((r) => {
          const productList = Array.isArray(r.data?.products)
            ? r.data.products
            : [];
          setResults((prev) =>
            page === 1 ? productList : [...prev, ...productList]
          );
          setTotal(Number(r.data?.total) || 0);
          setPages(Math.max(Number(r.data?.pages) || 1, 1));
        })
        .catch(() => {
          setResults([]);
          setTotal(0);
          setPages(1);
        })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [queryParams, store.ready]);

  useEffect(() => {
    setPage(1);
  }, [selectedMarket, effectiveCategory, q, country, brand, currencyF, platform, minPrice, maxPrice, avail, sort]);

  if (invalidMarket) {
    return (
      <div className="neon-page"><div className="vault-container pt-28 text-center" data-testid="market-not-found">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Unknown market</h1>
        <p className="mt-2 text-sm text-muted-foreground">Pick a market from the header to keep browsing.</p>
      </div></div>
    );
  }

  if (!store.ready) return null;

  const brands = [...new Set(Object.values(store.productsMap).map((p) => p.brand).filter(Boolean))].sort();
  const platforms = [...new Set(Object.values(store.productsMap).map((p) => p.platform).filter(Boolean))].sort();
  const countryOptions = selectedMarketObj && !selectedMarketObj.global
    ? store.countries.filter((c) => selectedMarketObj.countries?.includes(c.code))
    : store.countries.filter((c) => c.code !== "EU");

  const h1 = effectiveCategory
    ? `${store.categoryName(effectiveCategory)} — ${selectedMarketObj ? selectedMarketObj.name : "All markets"}`
    : selectedMarketObj?.seo?.h1 || "All Gift Cards";
  const desc = effectiveCategory
    ? `${store.categoryName(effectiveCategory)} gift cards ${selectedMarketObj ? `redeemable in ${selectedMarketObj.name}` : "across all markets"}.`
    : selectedMarketObj?.seo?.description ||
      "The full Zeyinx catalog. Choose a market to see only products redeemable there.";

  const goToCategory = (slug) => {
    if (selectedMarketObj) {
      navigate(`/market/${selectedMarketObj.slug}/${slug}`);
    } else if (slug) {
      navigate(`/cards?category=${slug}`);
    } else {
      navigate("/cards");
    }
  };

  const goToAll = () => {
    if (selectedMarketObj) navigate(`/market/${selectedMarketObj.slug}`);
    else navigate("/cards");
  };

  return (
    <div className="neon-page min-h-screen"><div className="vault-container pt-14 sm:pt-16" data-testid="market-page">
      <Reveal>
        <p className="overline-label">
          {selectedMarketObj ? `${selectedMarketObj.flag} ${selectedMarketObj.name} marketplace` : "Full catalog"}
        </p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tighter sm:text-4xl" data-testid="market-h1">
          {h1}
        </h1>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">{desc}</p>
        {!effectiveCategory && selectedMarketObj?.seo?.hero && (
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-primary/70" data-testid="market-hero-copy">
            {selectedMarketObj.seo.hero}
          </p>
        )}
        {!selectedMarketObj && (
          <button
            data-testid="market-page-choose"
            onClick={store.openMarketModal}
            className="mt-5 rounded-full border border-primary/40 bg-primary/[0.08] px-5 py-2.5 text-sm text-primary transition-colors duration-200 hover:bg-primary/[0.14]"
          >
            🌐 Choose your market to see what's redeemable for you
          </button>
        )}
      </Reveal>

      <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="category-chips">
        <button
          data-testid="category-all"
          onClick={goToAll}
          className={`rounded-full border px-4 py-2 text-xs transition-colors duration-200 ${
            !effectiveCategory
              ? "border-primary/60 bg-primary/10 text-foreground"
              : "border-white/10 text-muted-foreground hover:border-white/25 hover:text-foreground"
          }`}
        >
          All
        </button>
        {store.categories.map((c) => (
          <button
            key={c.slug}
            data-testid={`category-${c.slug}`}
            onClick={() => goToCategory(c.slug)}
            className={`rounded-full border px-4 py-2 text-xs transition-colors duration-200 ${
              effectiveCategory === c.slug
                ? "border-primary/60 bg-primary/10 text-foreground"
                : "border-white/10 text-muted-foreground hover:border-white/25 hover:text-foreground"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="market-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products, brands, platforms…"
            aria-label="Search products"
            className="w-full rounded-full border border-white/10 bg-white/[0.03] py-2.5 pl-10 pr-4 text-sm outline-none transition-colors duration-200 placeholder:text-muted-foreground/60 focus:border-primary/50"
          />
        </div>
        <button
          data-testid="filters-toggle"
          onClick={() => setShowFilters((s) => !s)}
          aria-expanded={showFilters}
          className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2.5 text-xs text-muted-foreground transition-colors duration-200 hover:border-white/25 hover:text-foreground"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
        </button>
        <select aria-label="Sort products" data-testid="sort-products" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-full border border-white/10 bg-background px-4 py-2.5 text-xs text-muted-foreground"><option value="popular">Sort: Popular</option><option value="price_asc">Price: low to high</option><option value="price_desc">Price: high to low</option><option value="name_asc">Name: A–Z</option><option value="newest">Newest</option></select>
        <span className="text-xs text-muted-foreground" data-testid="results-count">
          {total} product{total === 1 ? "" : "s"}
        </span>
      </div>

      {showFilters && (
        <div className="mt-4 grid grid-cols-2 gap-2 rounded-2xl border border-white/[0.08] bg-card p-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="filters-panel">
          <select aria-label="Country" data-testid="filter-country" value={country} onChange={(e) => setCountry(e.target.value)} className="rounded-lg border border-white/10 bg-background px-3 py-2 text-xs">
            <option value="">Country: all</option>
            {countryOptions.map((c) => (
              <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
            ))}
          </select>
          <select aria-label="Brand" data-testid="filter-brand" value={brand} onChange={(e) => setBrand(e.target.value)} className="rounded-lg border border-white/10 bg-background px-3 py-2 text-xs">
            <option value="">Brand: all</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select aria-label="Currency" data-testid="filter-currency" value={currencyF} onChange={(e) => setCurrencyF(e.target.value)} className="rounded-lg border border-white/10 bg-background px-3 py-2 text-xs">
            <option value="">Currency: all</option>
            {Object.keys(store.currencies).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select aria-label="Platform" data-testid="filter-platform" value={platform} onChange={(e) => setPlatform(e.target.value)} className="rounded-lg border border-white/10 bg-background px-3 py-2 text-xs">
            <option value="">Platform: all</option>
            {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select aria-label="Availability" data-testid="filter-availability" value={avail} onChange={(e) => setAvail(e.target.value)} className="rounded-lg border border-white/10 bg-background px-3 py-2 text-xs">
            <option value="">Availability: all</option>
            <option value="in">In stock</option>
            <option value="out">Unavailable</option>
          </select>
          <div className="flex items-center gap-1.5">
            <input aria-label="Minimum price" data-testid="filter-min-price" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} placeholder="Min" inputMode="numeric" className="w-full rounded-lg border border-white/10 bg-background px-2.5 py-2 text-xs" />
            <span className="text-muted-foreground">–</span>
            <input aria-label="Maximum price" data-testid="filter-max-price" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Max" inputMode="numeric" className="w-full rounded-lg border border-white/10 bg-background px-2.5 py-2 text-xs" />
          </div>
        </div>
      )}

      <div className="mt-10">
        {!bannerHidden && (
          <div className={`relative mb-8 overflow-hidden rounded-2xl border border-primary/30 bg-card ${banner.cta_url ? "cursor-pointer" : ""}`} data-testid="market-promo-banner" role="region" aria-label="Promotion">
            {banner.image_url ? (
              <button type="button" className="block w-full text-left" onClick={() => { if (banner.cta_url) { if (banner.cta_url.startsWith("/")) navigate(banner.cta_url); else window.open(banner.cta_url, "_blank", "noopener,noreferrer"); } }}>
                <img src={banner.image_url} alt={banner.text || "ZEYINX promotion"} className="block h-auto w-full object-contain" />
              </button>
            ) : (
              <div className="flex items-center gap-4 bg-gradient-to-r from-primary/[0.14] to-transparent px-5 py-4">
                <Sparkles className="h-4 w-4 shrink-0 text-primary" />
                <p className="flex-1 text-sm text-foreground">{banner.text}</p>
                {banner.cta_url && banner.cta_label && <button type="button" onClick={() => { if (banner.cta_url.startsWith("/")) navigate(banner.cta_url); else window.open(banner.cta_url, "_blank", "noopener,noreferrer"); }} className="shrink-0 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground">{banner.cta_label}</button>}
              </div>
            )}
            <button data-testid="promo-banner-dismiss" aria-label="Dismiss promotion" onClick={() => { sessionStorage.setItem(`zey_banner_${banner.id}`, "1"); setBannerDismissed(true); }} className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white backdrop-blur transition-colors hover:bg-black/70">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {loading && page === 1 ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : results.length === 0 ? (
          <div className="rounded-2xl border border-white/[0.08] bg-card p-14 text-center" data-testid="market-empty">
            <p className="font-display text-lg tracking-tight">No products available here yet</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {selectedMarketObj ? `Nothing in the catalog is currently redeemable in ${selectedMarketObj.name} with these filters.` : "Nothing matches these filters."}
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-testid="market-grid">
              {results.map((p, i) => <ProductCard key={`${p.slug}-${p.catalogDenomination || "base"}`} product={p} index={i} />)}
            </div>
            {page < pages && (
              <div className="mt-12 text-center">
                <button data-testid="load-more" onClick={() => setPage((p) => p + 1)} className="rounded-full border border-white/15 px-6 py-2.5 text-sm text-muted-foreground transition-colors duration-200 hover:border-white/30 hover:text-foreground">
                  {loading ? "Loading…" : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  </div>
  );

}
